import {
  AlertCircle,
  Building2,
  FolderOpen,
  Hourglass,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { ClientStats } from "@/features/clients/types";
import { cn } from "@/lib/utils";

interface CardConfig {
  key: keyof ClientStats;
  label: string;
  icon: LucideIcon;
  iconClassName: string;
}

const cards: CardConfig[] = [
  { key: "total", label: "Total Clients", icon: Users, iconClassName: "bg-primary/10 text-primary" },
  {
    key: "activeCases",
    label: "Active Cases",
    icon: FolderOpen,
    iconClassName: "bg-warning/10 text-warning",
  },
  {
    key: "businesses",
    label: "Businesses",
    icon: Building2,
    iconClassName: "bg-muted text-muted-foreground",
  },
  { key: "individuals", label: "Individuals", icon: User, iconClassName: "bg-muted text-muted-foreground" },
  {
    key: "pendingOnboarding",
    label: "Pending Onboarding",
    icon: Hourglass,
    iconClassName: "bg-info/10 text-info",
  },
  {
    key: "requiringAttention",
    label: "Requiring Attention",
    icon: AlertCircle,
    iconClassName: "bg-destructive/10 text-destructive",
  },
];

const numberFormatter = new Intl.NumberFormat("en-US");

/** The six practice-wide counts as one edge-to-edge band (mockup "Client
 * overview"), with vertical dividers between cells. */
export function ClientStatCards({
  stats,
  loading,
}: {
  stats: ClientStats | null;
  loading: boolean;
}) {
  return (
    <Card className="grid grid-cols-2 gap-0 overflow-hidden p-0 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card, i) => (
        <div
          key={card.key}
          className={cn(
            "flex flex-col gap-2.5 px-5 py-5",
            i > 0 && "border-t sm:border-t-0",
            (i + 1) % 3 !== 0 && "sm:border-r",
            i < cards.length - 1 && "lg:border-r",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              {card.label}
            </span>
            <span className={cn("flex size-7 items-center justify-center rounded-lg", card.iconClassName)}>
              <card.icon className="size-4" />
            </span>
          </div>
          {loading && stats === null ? (
            <Skeleton className="h-9 w-14" />
          ) : (
            <span className="font-heading text-foreground text-[2.25rem] leading-none font-semibold tabular-nums">
              {stats ? numberFormatter.format(stats[card.key]) : "—"}
            </span>
          )}
        </div>
      ))}
    </Card>
  );
}
